import { METRIKA_GOALS, YANDEX_METRIKA_ID, isDoNotTrackEnabled, isProdLikeHost } from '../config/metrics';
import { recordClientEvent } from './measurementEvents';
import { acquisitionAllowed } from './measurementContext';

const PRIMARY_TAG_URL = 'https://mc.yandex.ru/metrika/tag.js';
const FALLBACK_TAG_URL = 'https://mc.yandex.com/metrika/tag.js';
const SCRIPT_RETRY_COOLDOWN_MS = 60_000;
const __scriptCache = new Map(); // url -> Promise<boolean>

let initQueued = false;
let __scriptLoadPromise = null;
let __scriptLoadResult = null; // true | false | null
let __scriptLoadFailedAt = 0;
let __lastHitPath = '';
let __lastHitAt = 0;

// Dedupe store to avoid React StrictMode double-effects / double-click noise
const __dedupe = new Map(); // key -> ts
let __dedupeSweepAt = 0;

function stable(value) {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {};
    Object.keys(value).sort().forEach((k) => { out[k] = stable(value[k]); });
    return out;
  }
  return value;
}

function stableJSONStringify(value) {
  try { return JSON.stringify(stable(value)); } catch { return ''; }
}

function shouldSendDedupe(key, ttlMs) {
  if (!ttlMs || ttlMs <= 0) return true;
  try {
    const now = Date.now();
    const prev = __dedupe.get(key) || 0;
    if (now - prev < ttlMs) return false;
    __dedupe.set(key, now);

    // occasional cleanup to avoid unbounded growth
    if (__dedupe.size > 800 && now - __dedupeSweepAt > 10_000) {
      __dedupeSweepAt = now;
      for (const [k, t] of __dedupe.entries()) {
        if (now - t > 60_000) __dedupe.delete(k);
      }
    }
    return true;
  } catch {
    return true;
  }
}

export function shouldInitMetrika() {
  // Preserve the existing counter behavior. New first-party optional events use
  // their own separate consent gate; the informational cookie notice is unchanged.
  return isProdLikeHost() && !isDoNotTrackEnabled();
}

function isDebug() {
  try {
    const url = new URL(String(window.location.href || ''), window.location.origin);
    if (url.searchParams.get('ymdebug') === '1') {
      // Persist across reloads (login flow does a hard reload and would drop URL params)
      try { localStorage.setItem('aiDiary.ym.debug', '1'); } catch {}
      return true;
    }
    return localStorage.getItem('aiDiary.ym.debug') === '1';
  } catch {
    return false;
  }
}

function debugLog(kind, ...args) {
  if (!isDebug()) return;
  try {
    // eslint-disable-next-line no-console
    (console[kind] || console.log).apply(console, args);
  } catch {}
}

// Persistent debug buffer (survives reloads) for smoke-tests.
// Stored only when ymdebug is enabled.
const __DEBUG_LOG_KEY__ = 'aiDiary.ym.debug.log.v1';
let __debugBuffer = null;

function readDebugBuffer() {
  try {
    if (typeof sessionStorage === 'undefined') return [];
    const raw = sessionStorage.getItem(__DEBUG_LOG_KEY__);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function flushDebugBuffer() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const buf = Array.isArray(__debugBuffer) ? __debugBuffer : [];
    const trimmed = buf.length > 400 ? buf.slice(buf.length - 400) : buf;
    sessionStorage.setItem(__DEBUG_LOG_KEY__, JSON.stringify(trimmed));
  } catch {
    // ignore
  }
}

function pushDebugEvent(entry) {
  if (!isDebug()) return;
  try {
    if (!Array.isArray(__debugBuffer)) __debugBuffer = readDebugBuffer();
    __debugBuffer.push(entry);
    if (__debugBuffer.length > 450) __debugBuffer = __debugBuffer.slice(__debugBuffer.length - 400);
    flushDebugBuffer();
  } catch {
    // ignore
  }
}

export function getMetrikaDebugLog() {
  try {
    if (!Array.isArray(__debugBuffer)) __debugBuffer = readDebugBuffer();
    return Array.isArray(__debugBuffer) ? [...__debugBuffer] : [];
  } catch {
    return [];
  }
}

export function clearMetrikaDebugLog() {
  try {
    __debugBuffer = [];
    flushDebugBuffer();
  } catch {
    // ignore
  }
}

function ensureYmStub() {
  if (typeof window === 'undefined') return false;
  try {
    if (!window.ym) {
      // Official snippet stub: queues calls until tag.js upgrades it
      window.ym = function() {
        (window.ym.a = window.ym.a || []).push(arguments);
      };
      window.ym.l = 1 * new Date();
    }
    return typeof window.ym === 'function';
  } catch {
    return false;
  }
}

function rememberUtmFromUrl() {
  if (!shouldInitMetrika()) return;
  try {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') return;
    const url = new URL(String(window.location.href || ''), window.location.origin);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'];
    const picked = {};
    let hasAny = false;
    for (const k of utmKeys) {
      const v = url.searchParams.get(k);
      if (/^[A-Za-z0-9_.-]{1,96}$/.test(v || '')) {
        picked[k] = v;
        if (String(v).trim()) hasAny = true;
      }
    }
    if (hasAny) {
      sessionStorage.setItem('aiDiary.utm.v1', JSON.stringify({ savedAt: Date.now(), ...picked }));
    }
  } catch {
    // ignore
  }
}

function getUtmParams() {
  const empty = { utm_source: '', utm_medium: '', utm_campaign: '', utm_content: '', utm_term: '' };
  try {
    if (typeof window === 'undefined') return empty;
    // Prefer saved UTMs from the entry URL (SPA-safe)
    try {
      const raw = sessionStorage.getItem('aiDiary.utm.v1');
      const saved = raw ? JSON.parse(raw) : null;
      if (saved && typeof saved === 'object') {
        return {
          ...Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].map((key) => [key, /^[A-Za-z0-9_.-]{1,96}$/.test(saved[key] || '') ? saved[key] : ''])),
        };
      }
    } catch {}

    const url = new URL(String(window.location.href || ''), window.location.origin);
    return {
      ...Object.fromEntries(['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].map((key) => [key, /^[A-Za-z0-9_.-]{1,96}$/.test(url.searchParams.get(key) || '') ? url.searchParams.get(key) : ''])),
    };
  } catch {
    return empty;
  }
}

function loadScriptFrom(url) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve(false);
  }
  // Ensure official stub before loading
  ensureYmStub();

  const u = String(url || '').trim();
  if (!u) return Promise.resolve(false);

  const existing = __scriptCache.get(u);
  if (existing) return existing;

  const p = new Promise((resolve) => {
    const s = document.createElement('script');
    s.async = true;
    s.src = u;
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    try {
      (document.head || document.documentElement).appendChild(s);
    } catch {
      const ref = document.getElementsByTagName('script')[0] || document.head?.firstChild || null;
      (ref?.parentNode || document.head || document.body || document.documentElement).insertBefore(s, ref);
    }
  }).then((ok) => {
    // If load failed, allow retry later.
    if (!ok) {
      try { __scriptCache.delete(u); } catch {}
    }
    return !!ok;
  });

  __scriptCache.set(u, p);
  return p;
}

async function requestTagScriptLoad() {
  try {
    if (__scriptLoadResult === true) return true;

    const now = Date.now();
    if (__scriptLoadResult === false && (now - (__scriptLoadFailedAt || 0)) < SCRIPT_RETRY_COOLDOWN_MS) {
      return false;
    }

    if (__scriptLoadPromise) return __scriptLoadPromise;

    __scriptLoadPromise = (async () => {
      const primaryOk = await loadScriptFrom(PRIMARY_TAG_URL);
      if (primaryOk) return true;
      if (!shouldInitMetrika()) return false;
      debugLog('warn', '[YM] primary tag.js failed to load, trying fallback (.com)');
      const fallbackOk = await loadScriptFrom(FALLBACK_TAG_URL);
      return !!fallbackOk;
    })();

    const ok = await __scriptLoadPromise;
    __scriptLoadPromise = null;
    __scriptLoadResult = !!ok;
    if (!ok) {
      __scriptLoadFailedAt = Date.now();
      debugLog('error', '[YM] tag.js failed to load from both domains, sending watch pixel');
      sendWatchPixel();
    }
    return !!ok;
  } catch {
    __scriptLoadPromise = null;
    __scriptLoadResult = false;
    __scriptLoadFailedAt = Date.now();
    try { sendWatchPixel(); } catch {}
    return false;
  }
}

function sendWatchPixel() {
  try {
    if (!shouldInitMetrika()) return;
    if (!YANDEX_METRIKA_ID || !Number.isFinite(Number(YANDEX_METRIKA_ID))) return;
    const rn = Date.now();
    const urls = [
      `https://mc.yandex.ru/watch/${YANDEX_METRIKA_ID}?rn=${rn}`,
      `https://mc.yandex.com/watch/${YANDEX_METRIKA_ID}?rn=${rn}`,
    ];
    for (const src of urls) {
      try {
        const img = new Image();
        img.src = src;
      } catch {}
    }
  } catch {
    // ignore
  }
}

function ensureInitQueued() {
  try {
    // Existing Metrika tags and optional first-party attribution are independent.
    rememberUtmFromUrl();

    if (!shouldInitMetrika()) return false;
    if (!YANDEX_METRIKA_ID || !Number.isFinite(Number(YANDEX_METRIKA_ID))) return false;

    // Start loading tag.js in the background (with fallback)
    void requestTagScriptLoad();

    if (!ensureYmStub()) return false;
    if (initQueued) return true;

    // Queue init ASAP (official snippet order: init first, then hits/goals/userParams).
    window.ym(YANDEX_METRIKA_ID, 'init', {
      defer: true,
      // Preserve the established counter settings and UI privacy masks.
      webvisor: true,
      clickmap: true,
      accurateTrackBounce: true,
      trackLinks: true,
      trackHash: true,
    });
    initQueued = true;
    debugLog('info', '[YM] init() queued');

    // Initial user params (SPA-safe UTMs)
    try {
      const nav = typeof navigator !== 'undefined' ? navigator : null;
      const utm = getUtmParams();
      const params = {
        app: 'web',
        locale: (nav && (nav.language || (Array.isArray(nav.languages) && nav.languages[0]))) || 'ru',
        platform: (nav && (nav.userAgentData && nav.userAgentData.platform)) || (nav && nav.platform) || '',
        ...utm,
      };
      window.ym(YANDEX_METRIKA_ID, 'userParams', params);
      debugLog('info', '[YM] userParams(init)', params);
      pushDebugEvent({ ts: Date.now(), type: 'userParams', params, path: (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })() });
    } catch {}

    return true;
  } catch {
    return false;
  }
}

export async function initMetrikaIfAllowed() {
  // Always preserve UTMs from the entry URL (SPA-safe), even if user consents later
  rememberUtmFromUrl();

  if (!shouldInitMetrika()) return false;
  if (!YANDEX_METRIKA_ID || !Number.isFinite(Number(YANDEX_METRIKA_ID))) return false;

  debugLog('info', '[YM] init requested: consent ok, DNT off, host ok');
  const queued = ensureInitQueued();
  const loaded = await requestTagScriptLoad();
  return !!queued && !!loaded;
}

function goalDedupeTtlMs(name) {
  try {
    const n = String(name || '');
    if (!n) return 0;

    // Route/view-like events: can be duplicated by SPA effects / StrictMode.
    if (n.startsWith('view_')) return 1500;
    if (n.endsWith('_view')) return 1500; // e.g. analytics_view
    if (n === 'share_link_opened') return 2000;

    // Success events: dedupe only very short (protect from double-submit / double-handler)
    if (n.endsWith('_success')) return 1200;

    // Error events: keep dedupe minimal so repeated attempts aren't cut
    if (n.endsWith('_error')) return 350;

    // Registration steps are often triggered by UI + network race; keep short dedupe.
    if (n === 'registration_started' || n === 'registration_verified') return 1200;
    // Billing actions may be retried by React effects or payment-return handlers.
    if (n === 'checkout_started') return 5000;
    if (n === 'trial_started' || n === 'payment_succeeded') return 60_000;
    if (n === 'code_download_clicked') return 1500;
    if (n === 'mobile_store_clicked' || n === 'mobile_release_updates_clicked') return 1500;
    return 0;
  } catch {
    return 0;
  }
}

export function trackGoal(name, params, options) {
  try {
    if (!name) return false;
    if (name === 'billing_view') recordClientEvent('paywall_view', 'billing');
    if (!ensureInitQueued()) return false;

    const ttl = goalDedupeTtlMs(name);
    if (ttl) {
      const path = (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })();
      const internalDedupeKey = String(options?.dedupeKey || '').trim();
      const key = internalDedupeKey
        ? `goal:${String(name)}:internal:${internalDedupeKey}`
        : `goal:${String(name)}:${path}:${stableJSONStringify(params || null)}`;
      if (!shouldSendDedupe(key, ttl)) {
        debugLog('info', '[YM] deduped goal', name, params || undefined);
        pushDebugEvent({ ts: Date.now(), type: 'goal', name: String(name), params: params || null, path, deduped: true });
        return true;
      }
    }

    if (params && typeof params === 'object') {
      window.ym(YANDEX_METRIKA_ID, 'reachGoal', name, params);
      debugLog('info', '[YM] goal', name, params);
      pushDebugEvent({ ts: Date.now(), type: 'goal', name: String(name), params, path: (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })(), deduped: false });
    } else {
      window.ym(YANDEX_METRIKA_ID, 'reachGoal', name);
      debugLog('info', '[YM] goal', name);
      pushDebugEvent({ ts: Date.now(), type: 'goal', name: String(name), params: null, path: (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })(), deduped: false });
    }
    return true;
  } catch {
    return false;
  }
}

// Helper for flows that are followed by a hard reload/redirect.
// Attempts to wait for Metrika to acknowledge processing the goal via callback,
// but always resolves within `timeoutMs` to avoid blocking UX when tag.js is blocked.
export async function trackGoalAndWait(name, params, options) {
  try {
    if (!name) return false;
    if (!ensureInitQueued()) return false;

    const timeoutMs = (() => {
      try {
        const v = Number(options && options.timeoutMs);
        return (Number.isFinite(v) && v > 0) ? Math.floor(v) : 650;
      } catch {
        return 650;
      }
    })();

    const ttl = goalDedupeTtlMs(name);
    if (ttl) {
      const path = (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })();
      const key = `goal:${String(name)}:${path}:${stableJSONStringify(params || null)}`;
      if (!shouldSendDedupe(key, ttl)) {
        debugLog('info', '[YM] deduped goal(wait)', name, params || undefined);
        pushDebugEvent({ ts: Date.now(), type: 'goal', name: String(name), params: params || null, path, deduped: true });
        return true;
      }
    }

    return await new Promise((resolve) => {
      let done = false;
      const finish = (ok) => {
        if (done) return;
        done = true;
        try { clearTimeout(t); } catch {}
        resolve(!!ok);
      };

      const t = setTimeout(() => finish(false), timeoutMs);

      try {
        const path = (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })();
        const cb = () => finish(true);

        if (params && typeof params === 'object') {
          window.ym(YANDEX_METRIKA_ID, 'reachGoal', name, params, cb);
          debugLog('info', '[YM] goal(wait)', name, params);
          pushDebugEvent({ ts: Date.now(), type: 'goal', name: String(name), params, path, deduped: false, wait: true });
        } else {
          window.ym(YANDEX_METRIKA_ID, 'reachGoal', name, cb);
          debugLog('info', '[YM] goal(wait)', name);
          pushDebugEvent({ ts: Date.now(), type: 'goal', name: String(name), params: null, path, deduped: false, wait: true });
        }
      } catch {
        finish(false);
      }
    });
  } catch {
    return false;
  }
}

// Diagnostic visit parameters, deliberately not goals/conversions. No free text,
// prompts, images, account identifiers or raw URLs are accepted here.
export function trackLandingDemo(pageKey, language, action, scenario = 'default') {
  const pages = ['aiAssistant', 'aiPlanner', 'aiDiary', 'aiForWork', 'aiForProductivity', 'aiForStudents', 'aiPhotoEditor'];
  if (!pages.includes(pageKey) || !['ru', 'en'].includes(language) || !['view', 'start', 'complete', 'interact', 'cta'].includes(action)) return false;
  if (!['default', '0', '1', '2', 'meeting', 'reminder', 'recurring', 'background', 'before', 'after', 'logo'].includes(String(scenario))) return false;
  try {
    if (!acquisitionAllowed()) return false;
    if (!ensureInitQueued()) return false;
    window.ym(YANDEX_METRIKA_ID, 'params', {
      landing_demo_v1: { [pageKey]: { [language]: { [action]: String(scenario) } } },
    });
    return true;
  } catch { return false; }
}

export function trackUserParams(params) {
  try {
    if (!params || typeof params !== 'object') return;
    if (!ensureInitQueued()) return;

    window.ym(YANDEX_METRIKA_ID, 'userParams', params);
    debugLog('info', '[YM] userParams', params);
    pushDebugEvent({ ts: Date.now(), type: 'userParams', params, path: (() => { try { return String(window.location.pathname || '') + String(window.location.search || '') + String(window.location.hash || ''); } catch { return ''; } })() });
  } catch {
    // ignore
  }
}

function readMetrikaClientIdCookie() {
  try {
    if (typeof document === 'undefined') return '';
    const match = String(document.cookie || '').match(/(?:^|;\s*)_ym_uid=([^;]+)/);
    const value = match ? decodeURIComponent(match[1] || '').trim() : '';
    return /^\d{6,32}$/.test(value) ? value : '';
  } catch {
    return '';
  }
}

// Returns the anonymous browser identifier recommended by Yandex for linking
// confirmed server-side payments to the acquisition session. Never returns PII.
export async function getMetrikaClientId(options) {
  try {
    if (!ensureInitQueued()) return '';
    const timeoutMs = (() => {
      const raw = Number(options?.timeoutMs);
      return Number.isFinite(raw) && raw > 0 ? Math.min(3000, Math.floor(raw)) : 1500;
    })();

    return await new Promise((resolve) => {
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        try { clearTimeout(timer); } catch {}
        const normalized = String(value || '').trim();
        resolve(/^\d{6,32}$/.test(normalized) ? normalized : readMetrikaClientIdCookie());
      };
      const timer = setTimeout(() => finish(''), timeoutMs);
      try {
        window.ym(YANDEX_METRIKA_ID, 'getClientID', (clientId) => finish(clientId));
      } catch {
        finish('');
      }
    });
  } catch {
    return '';
  }
}

// Links the existing anonymous ClientID with a stable non-PII account hash.
// This enables future attribution of verified App Store / Google Play payments.
export function setMetrikaUserId(userIdHash) {
  try {
    const value = String(userIdHash || '').trim().toLowerCase();
    if (!/^[a-f0-9]{64}$/.test(value)) return false;
    if (!ensureInitQueued()) return false;
    window.ym(YANDEX_METRIKA_ID, 'setUserID', value);
    debugLog('info', '[YM] setUserID', `${value.slice(0, 8)}…`);
    pushDebugEvent({ ts: Date.now(), type: 'setUserID', userIdHashPrefix: value.slice(0, 8) });
    return true;
  } catch {
    return false;
  }
}

export function trackPageView(path, options) {
  try {
    // Never send share tokens, chat IDs, search text or arbitrary query strings.
    const sourceUrl = new URL(String(path || '/'), window.location.origin);
    const raw = sourceUrl.pathname;
    const parts = raw.split('/').filter(Boolean);
    const locale = parts[0] === 'en' ? '/en' : '';
    if (locale) parts.shift();
    const allowed = new Set(['welcome', 'ai-assistant', 'ai-photo-editor', 'ai-planner', 'ai-diary', 'ai-for-work', 'ai-for-productivity', 'ai-for-students', 'code', 'mobile', 'about', 'pricing', 'login', 'privacy', 'terms', 'cookies', 'news', 'publications', 'chat', 'diary', 'calendar', 'tracker', 'analytics', 'settings', 'brain', 's', 'billing']);
    const firstPart = parts[0] === 'ai-calendar' ? 'ai-planner' : parts[0];
    const page = parts.length ? (allowed.has(firstPart) ? firstPart : 'other') : '';
    let p = `${locale}/${page}`;
    // Keep campaign matching on public landings; private route IDs/queries stay out.
    if (['', 'welcome', 'ai-assistant', 'ai-photo-editor', 'ai-planner', 'ai-diary', 'ai-for-work', 'ai-for-productivity', 'ai-for-students', 'code', 'mobile', 'pricing', 'about'].includes(page)) {
      const query = new URLSearchParams();
      for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content']) {
        const value = sourceUrl.searchParams.get(key) || '';
        if (/^[A-Za-z0-9_.-]{1,96}$/.test(value)) query.set(key, value);
      }
      const yclid = sourceUrl.searchParams.get('yclid') || '';
      if (/^[0-9]{1,40}$/.test(yclid)) query.set('yclid', yclid);
      if (query.toString()) p += `?${query}`;
    }
    if (['chat', 'diary', 'calendar', 'tracker', 'analytics', 'settings'].includes(page)) recordClientEvent('screen_view', page);
    if (!p) return;

    if (!ensureInitQueued()) return;

    // Dedupe only consecutive duplicates (StrictMode double-effects / double-calls),
    // but DO NOT block real navigation like A -> B -> A even if it happens quickly.
    const now = Date.now();
    if (p === __lastHitPath && (now - (__lastHitAt || 0)) < 900) {
      debugLog('info', '[YM] deduped hit', p);
      pushDebugEvent({ ts: now, type: 'hit', path: p, options: options || null, deduped: true });
      return;
    }
    __lastHitPath = p;
    __lastHitAt = now;

    let referer = window.location.origin;
    try { if (document.referrer) referer = new URL(document.referrer).origin; } catch {}
    window.ym(YANDEX_METRIKA_ID, 'hit', p, { title: `Noesa ${page || 'home'}`, referer });
    debugLog('info', '[YM] hit', p, options || undefined);
    pushDebugEvent({ ts: now, type: 'hit', path: p, options: options || null, deduped: false });
  } catch {
    // ignore
  }
}

// Backward-compatible aliases (avoid direct ym() outside this module)
export function ymHit(path, options) { trackPageView(path, options); }
export function ymGoal(name, params) { trackGoal(name, params); }
export function ymUserParams(params) { trackUserParams(params); }

export function exposeMetrikaDebugTools() {
  try {
    if (typeof window === 'undefined') return;
    const isDevEnv = process.env.NODE_ENV !== 'production';
    if (!isDevEnv && !isDebug()) return;
    if (window.__noesaMetrika) return;

    window.__noesaMetrika = Object.freeze({
      counterId: YANDEX_METRIKA_ID,
      shouldInit: shouldInitMetrika,
      init: initMetrikaIfAllowed,
      hit: trackPageView,
      goal: trackGoal,
      userParams: trackUserParams,
      getClientID: getMetrikaClientId,
      setUserID: setMetrikaUserId,
      getLog: getMetrikaDebugLog,
      clearLog: clearMetrikaDebugLog,
      printLog: () => {
        try {
          const rows = getMetrikaDebugLog();
          const goals = {};
          let hits = 0;
          let dedupedHits = 0;
          let dedupedGoals = 0;
          for (const r of rows) {
            if (r?.type === 'hit') {
              if (r?.deduped) dedupedHits += 1; else hits += 1;
            }
            if (r?.type === 'goal') {
              if (r?.deduped) dedupedGoals += 1;
              else goals[r.name] = (goals[r.name] || 0) + 1;
            }
          }
          // eslint-disable-next-line no-console
          console.groupCollapsed(`[YM] debug log: ${rows.length} rows (hits=${hits}, dedupedHits=${dedupedHits}, dedupedGoals=${dedupedGoals})`);
          // eslint-disable-next-line no-console
          console.table(goals);
          // eslint-disable-next-line no-console
          console.log('Last 30 rows:', rows.slice(-30));
          // eslint-disable-next-line no-console
          console.groupEnd();
          return { rows, hits, dedupedHits, goals, dedupedGoals };
        } catch {
          return null;
        }
      },
      smokeReport: () => {
        try {
          const rows = getMetrikaDebugLog().filter((r) => r && r.type === 'goal' && !r.deduped);
          const strong = [
            METRIKA_GOALS.view_welcome,
            METRIKA_GOALS.view_login,
            METRIKA_GOALS.login_error,
            METRIKA_GOALS.login_success,
            METRIKA_GOALS.record_created,
            METRIKA_GOALS.chat_message_sent,
            METRIKA_GOALS.analytics_view,
          ];
          const counts = {};
          for (const g of strong) counts[g] = 0;
          for (const r of rows) {
            if (counts[r.name] != null) counts[r.name] += 1;
          }
          const bad = Object.fromEntries(Object.entries(counts).filter(([, v]) => v !== 1));
          return { counts, ok: Object.keys(bad).length === 0, bad, totalGoals: rows.length };
        } catch {
          return null;
        }
      },
      enableDebug: () => { try { localStorage.setItem('aiDiary.ym.debug', '1'); } catch {} },
      disableDebug: () => { try { localStorage.removeItem('aiDiary.ym.debug'); } catch {} },
      test: async () => {
        const ok = await initMetrikaIfAllowed();
        trackGoal(METRIKA_GOALS.debug_ping, { ts: Date.now() });
        return { ok, counterId: YANDEX_METRIKA_ID };
      },
    });
  } catch {
    // ignore
  }
}


